import { useMemo, useCallback } from "react";
import { useDisclosure } from "@mantine/hooks";

import {
  WarningModal,
  type WarningModalProps,
} from "@/components/warning-modal/";

export interface UseWarningModalOptions {
  title?: string;
  text?: string;
  confirmText?: string;
  cancelText?: string;
  onConfirm: () => void;
  onCancel?: () => void;
}

const defaultModalConfig = {
  title: "Warning",
  text: "Are you sure you want to proceed?",
  confirmText: "Confirm",
  cancelText: "Cancel",
};

export function useWarningModal({
  onCancel,
  onConfirm,
  ...options
}: UseWarningModalOptions) {
  const [isOpen, { open: openModal, close: closeModal }] = useDisclosure(false);

  const getCloseWithCallback = useCallback(
    (cb?: () => void) => () => {
      cb?.();
      closeModal();
    },
    [closeModal],
  );

  const warningModal = useMemo(() => {
    const modalConfig: WarningModalProps = {
      ...defaultModalConfig,
      onCancel: getCloseWithCallback(onCancel),
      onConfirm: getCloseWithCallback(onConfirm),
      opened: isOpen,
      ...options,
    };

    return <WarningModal {...modalConfig} />;
  }, [getCloseWithCallback, isOpen, onCancel, onConfirm, options]);

  return {
    warningModal,
    isOpen,
    openWarningModal: openModal,
    closeWarningModal: closeModal,
  };
}
